import { useEffect, useRef, useState } from 'react';
import { ModalTitle } from '@overlayqa/shared/ui';
import { Outlet, NavLink, Navigate, Link, useLocation } from 'react-router-dom';
import { authEnabled } from '../lib/auth';
import { UserMenuButton } from './UserMenuButton';
import { useAppSession } from '../hooks/useAppSession';
import { useClerkPurchaseEnabled } from '../hooks/useClerkPurchaseEnabled';
import { clsx } from 'clsx';
import { HugeiconsIcon } from '@hugeicons/react';
import { Home01Icon } from '@hugeicons/core-free-icons';
import { Folder01Icon } from '@hugeicons/core-free-icons';
import { Settings02Icon } from '@hugeicons/core-free-icons';
import { PuzzleIcon } from '@hugeicons/core-free-icons';
import { Calendar03Icon } from '@hugeicons/core-free-icons';
import { ChartColumnIcon } from '@hugeicons/core-free-icons';
import { Clock01Icon } from '@hugeicons/core-free-icons';
import { CreditCardIcon } from '@hugeicons/core-free-icons';
import { Menu01Icon } from '@hugeicons/core-free-icons';
import { Logo, LogoMark } from './ui/Logo';
import { AppLoadingShell } from './AppLoadingShell';
import { LoadError } from './shared/IssuesLoadError';
import { log } from '@/lib/logger';
import { Toaster } from 'sonner';
import { DASHBOARD_TOASTER_PROPS } from '../lib/toasterConfig';
import { ToastAnnouncer } from './ToastAnnouncer';
import { OnboardingReviewNotice } from './OnboardingReviewNotice';
import { NotificationBell } from './NotificationBell';
import { TrialNudgeBanner } from './TrialNudgeBanner';
import { TrialStatusChip } from './TrialStatusChip';
import { UpgradeModal } from './billing/UpgradeModal';
import { WhatsNewModal } from './WhatsNewModal';
import { ExtensionInstallBanner } from './ExtensionInstallBanner';
import { DevGridProvider, DevGridOverlay } from './DevGridOverlay';
import { Tooltip } from './shared/Tooltip';
import { FeedbackFab } from './FeedbackFab';
import { DESKTOP_LAYOUT_QUERY } from '../lib/desktopLayout';
import { ProjectBreadcrumb } from './ProjectBreadcrumb';
import { accountAvatarAppearance } from './ui/accountAvatarAppearance';
import { ModalPortal } from './shared/ModalPortal';
import { useModalA11y } from '../hooks/useModalA11y';
import { useExtensionDetection } from '../hooks/useExtensionDetection';
import { CHROME_EXTENSION_URL } from '../lib/constants';
import { ChromeIcon } from './shared/ChromeIcon';
import { capture } from '../lib/analytics';
import { pendingOpenBelongsToSession, readPendingExtensionOpen } from '../lib/pendingExtensionOpen';

// Shared with the sessionError branch below so the two <UserButton> renders
// (the normal header, and the escape hatch on a broken account) cannot drift.
const USER_BUTTON_APPEARANCE = {
  elements: {
    footer: { display: 'none' },
  },
};

// The modal "Manage account" opens is Clerk's <UserProfile>, a separate
// component with its own appearance. Its Security page carries a "Delete
// account" action that demands reverification with a password, email code or
// phone code; 153 of 175 production users (87%, measured 2026-08-05) signed
// up with Google OAuth and hold none, so for them it dead-ends in
// "authentication factor is not configured" plus an "Email support" button
// (one person hit it 2026-09-02; scout inbox 01a06276). Settings > Delete
// account (DeleteAccountSection) deletes through the Backend API with no
// reverification, so it stays the one delete path and Clerk's is hidden.
// Keys measured off the rendered modal, not guessed: the button renders as
// `cl-profileSectionPrimaryButton__danger`, so the section id is `danger`.
// Pinned by Layout.userProfileDanger.source.test.ts; driven live by
// e2e/verify-clerk-delete-dead-end.ts.
const USER_PROFILE_PROPS = {
  appearance: {
    elements: {
      profileSection__danger: { display: 'none' },
      profileSectionPrimaryButton__danger: { display: 'none' },
    },
  },
};

export function Layout() {
  const { data: session, isLoading, isFetching, error: sessionError, refetch: refetchSession } = useAppSession();
  const purchaseThroughClerk = useClerkPurchaseEnabled();
  const { pathname } = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Reuse the global banner's detector for its persistent navigation entry.
  const extension = useExtensionDetection({ poll: true, intervalMs: 5000 });
  const showInstallLink = !extension.checking && !extension.detected;
  const userButtonAppearance = {
    ...USER_BUTTON_APPEARANCE,
    elements: { ...USER_BUTTON_APPEARANCE.elements, ...accountAvatarAppearance(session?.user) },
  };

  // Issue sync is now handled by useNotifications inside NotificationBell
  // (single SSE connection for both notifications and query cache invalidation)

  log.info('[Layout] onboarding guard', { isLoading, onboardingCompleted: session?.user?.onboardingCompleted });

  // Trial-expired users can still browse (projects, dashboard, settings).
  // Write actions (export, create issue, etc.) are blocked server-side by
  // checkWriteAccess returning TRIAL_EXPIRED. The TrialNudgeBanner shows a
  // persistent upgrade prompt at the top of every page.

  // Redirect to onboarding if not completed. /billing is exempt: the
  // trial_conversion emails deep-link there, and a pricing click that lands on
  // a 4-step wizard breaks the email's promise at peak intent (2026-10-09 read).
  // Framer also has a concrete destination: its handoff supplies project
  // creation and review setup without discarding the published page URL.
  // Recovery already resolved an accessible project. Keep its install/return
  // step reachable for new accounts without starting project creation again.
  const pendingRecovery = pathname === '/dashboard' ? readPendingExtensionOpen() : null;
  const continuingReview = pendingRecovery?.source === 'team_review' && pendingOpenBelongsToSession(pendingRecovery, session);
  if (!isLoading && session && !session.user.onboardingCompleted && pathname !== '/billing' && pathname !== '/integrations/framer' && !continuingReview) {
    log.info('[Layout] Onboarding not completed — redirecting to /onboarding');
    return <Navigate to="/onboarding" replace />;
  }

  // Show loading state while session is resolving to prevent empty shell flash
  if (isLoading) {
    return <AppLoadingShell />;
  }

  // The session request gates the whole app, so its failure needs somewhere to
  // land. Before /auth/bootstrap was bounded (see BOOTSTRAP_TIMEOUT_MS) a
  // request that stalled rather than failed never settled, isLoading never went
  // false, and the user sat on the animated logo forever with nothing to click:
  // the same dead end that stranded a real user on the review handoff spinner
  // on 2026-08-12 (GitHub issue #75), on the largest surface in the product.
  //
  // Keep the app's frame and static skeleton, without mounting routes or
  // account-dependent controls. Retry and the real account menu stay usable.
  //
  // The condition is "no session", NOT "an error happened". TanStack Query sets
  // `error` on any failed fetch while KEEPING the data it already has, and two
  // defaults make that the common case: useAppSession's staleTime is 5min and
  // nothing sets refetchOnWindowFocus, which defaults to true. So a tab
  // backgrounded past five minutes refetches on return, and one transient blip
  // would otherwise replace a working dashboard - open modal, half-typed form,
  // scroll position - with a full-page error while the cached session is still
  // valid. A session we already have is still usable; the failure belongs in
  // the background, not over the whole app.
  if (sessionError && !session) {
    return (
      <AppLoadingShell accountAction={
          <UserMenuButton afterSignOutUrl="/sign-in?signedOut=1" appearance={USER_BUTTON_APPEARANCE} userProfileProps={USER_PROFILE_PROPS} />
      }>
        <div className="max-w-xl">
          <LoadError
            resource="your account"
            error={sessionError as Error}
            onRetry={() => refetchSession()}
            retrying={isFetching}
          />
        </div>
      </AppLoadingShell>
    );
  }

  return (
    <DevGridProvider>
    <div className="min-h-screen flex bg-[var(--color-bg)]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-white focus:text-primary-600 focus:rounded-lg focus:shadow-lg focus:ring-2 focus:ring-primary-500 focus:text-sm focus:font-medium"
      >
        <span className="text-cap-trim block">Skip to main content</span>
      </a>
      {/* Sidebar: a white rail (Emily, 2026-10-09: "make app sidebar bg white")
          on the shared ground, with main's single divider as its only edge.
          The 2026-08-05 call against a DARKER tint stands in spirit: on white
          the primary-100 active pill and the gray-200 hover separate more, not
          less. No --color-rail token; the mobile drawer already paints
          bg-white, so the two nav surfaces now match. AppLoadingShell mirrors
          this rail (pinned by Layout.sidebarRail.test.tsx). */}
      <aside
        id="sidebar"
        className={clsx(
          'relative flex-col transition-all duration-200 hidden md:flex sticky top-0 h-screen bg-white',
          isCollapsed ? 'w-sidebar-collapsed' : 'w-sidebar'
        )}
      >
        {/* Logo */}
        {/* px-7 (28px) matches the nav rows' icon column: nav px-4 + row px-3. */}
        <header className={clsx('h-[72px] flex items-center', isCollapsed ? 'justify-center px-2' : 'px-7')}>
          <Link to="/dashboard" aria-label="Go to dashboard">
            {isCollapsed ? (
              <LogoMark className="w-6 h-6" />
            ) : (
              <Logo size="xs" />
            )}
          </Link>
        </header>

        {/* Navigation */}
        <nav className={clsx('flex-1 pb-4', isCollapsed ? 'px-0' : 'px-4')}>
          {/* Sidebar toggle — one hamburger, same spot in both states, above Dashboard */}
          <div className={clsx('mb-4', isCollapsed && 'flex justify-center')}>
            <Tooltip label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className={clsx(
                  'flex items-center rounded-lg text-sm font-medium transition-colors text-gray-600 hover:bg-gray-200 hover:text-gray-900',
                  isCollapsed ? 'justify-center w-9 h-9' : 'gap-3 px-3 py-2 w-full'
                )}
                aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <HugeiconsIcon icon={Menu01Icon} size={20} strokeWidth={2} color="currentColor" aria-hidden="true" />
                {!isCollapsed && <span>Collapse</span>}
              </button>
            </Tooltip>
          </div>
          <NavigationItems collapsed={isCollapsed} />
        </nav>
        {showInstallLink && <div className={clsx('pb-4', isCollapsed ? 'px-0' : 'px-4')}><InstallExtensionLink collapsed={isCollapsed} /></div>}
      </aside>

      {/* Main content */}
      {/* The rail/content divider lives HERE, not as border-r on the aside.
          On the aside it consumed 1px of the collapsed rail, leaving an odd
          content box that centred the icon on a half pixel. Hanging it off main
          keeps the rail's geometry whole. */}
      <main id="main" className="min-w-0 flex-1 overflow-x-clip app-ground relative md:border-l border-gray-200">
        <ExtensionInstallBanner {...extension} />
        <TrialNudgeBanner />
        <UpgradeModal />
        <WhatsNewModal />
        {!authEnabled && (
          <div className="bg-yellow-50 text-yellow-800 border-b border-yellow-200 px-6 py-2 text-sm">
            Auth disabled. Set VITE_CLERK_PUBLISHABLE_KEY to enable sign in.
          </div>
        )}
        {/* Shared header bar */}
        <header className="sticky top-0 z-10 app-ground px-4 md:px-8 grid grid-cols-[auto_minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 gap-y-2 min-h-[72px] py-3 md:py-0">
          <button
            type="button"
            className="btn-ghost md:hidden row-start-1 col-start-1"
            aria-label="Open navigation"
            aria-haspopup="dialog"
            aria-expanded={mobileNavOpen}
            aria-controls={mobileNavOpen ? 'mobile-navigation' : undefined}
            onClick={() => setMobileNavOpen(true)}
          >
            <HugeiconsIcon icon={Menu01Icon} size={20} strokeWidth={2} color="currentColor" aria-hidden="true" />
          </button>
          <div className="min-w-0 col-start-2 md:col-start-1 row-start-1">
            <PageTitle />
            <ProjectBreadcrumb />
          </div>
          {/* A trial has its own row on phones so the bell and avatar remain
              reachable. Paid/non-owner accounts render no chip or empty row. */}
          <TrialStatusChip className="col-start-2 col-span-2 row-start-2 justify-self-end md:col-span-1 md:row-start-1" />
          <div className="flex items-center gap-3 col-start-3 row-start-1">
            <NotificationBell />
            <UserMenuButton canManageSubscription={session?.team?.role === 'owner'} purchaseThroughClerk={purchaseThroughClerk} afterSignOutUrl="/sign-in?signedOut=1" appearance={userButtonAppearance} userProfileProps={USER_PROFILE_PROPS} />
          </div>
        </header>
        <Outlet />
        {/* Toasts offset up so they clear the bottom-right FeedbackFab. */}
        <Toaster {...DASHBOARD_TOASTER_PROPS} offset={{ bottom: '88px' }} />
        {/* Sonner's own wrapper is aria-live="polite", hardcoded with no prop
            to change it, so a refusal queued behind whatever the screen reader
            was saying. This carries the errors assertively. See
            ToastAnnouncer.tsx for why it is additive rather than a prop. */}
        <ToastAnnouncer />
        <OnboardingReviewNotice />
        <FeedbackFab pathname={pathname} />
        <DevGridOverlay />
      </main>
      <MobileNavigation isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} showInstallLink={showInstallLink} />
    </div>
    </DevGridProvider>
  );
}

function NavigationItems({ collapsed = false, onNavigate }: { collapsed?: boolean; onNavigate?: () => void }) {
  return (
    <ul className="space-y-2">
      <NavItem to="/dashboard" icon={<DashboardIcon />} collapsed={collapsed} onNavigate={onNavigate}>Dashboard</NavItem>
      <NavItem to="/projects" icon={<ProjectsIcon />} collapsed={collapsed} onNavigate={onNavigate} alsoMatchPaths={['/issues']}>Projects</NavItem>
      <NavItem to="/integrations" icon={<IntegrationsIcon />} collapsed={collapsed} onNavigate={onNavigate}>Integrations</NavItem>
      <NavItem to="/schedule" icon={<ScheduleIcon />} collapsed={collapsed} onNavigate={onNavigate}>Schedule</NavItem>
      <NavItem to="/reports" icon={<ReportsIcon />} collapsed={collapsed} onNavigate={onNavigate} comingSoon>Reports</NavItem>
      <NavItem to="/billing" icon={<BillingIcon />} collapsed={collapsed} onNavigate={onNavigate}>Billing</NavItem>
      <NavItem to="/settings" icon={<SettingsIcon />} collapsed={collapsed} onNavigate={onNavigate}>Account</NavItem>
    </ul>
  );
}

function InstallExtensionLink({ collapsed = false, onNavigate }: { collapsed?: boolean; onNavigate?: () => void }) {
  const link = (
    <a
      href={CHROME_EXTENSION_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Install Chrome extension"
      className={clsx('flex items-center rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors', collapsed ? 'justify-center w-9 h-9 mx-auto' : 'gap-3 px-3 py-2')}
      onClick={() => {
        capture('dashboard_extension_install_click', { source: 'navigation' });
        onNavigate?.();
      }}
    >
      <ChromeIcon className="w-5 h-5 shrink-0" />
      {!collapsed && <span>Install extension</span>}
    </a>
  );
  return collapsed ? <Tooltip label="Install Chrome extension" placement="right">{link}</Tooltip> : link;
}

function MobileNavigation({ isOpen, onClose, showInstallLink }: { isOpen: boolean; onClose: () => void; showInstallLink: boolean }) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const focusDesktopAfterClose = useRef(false);
  useModalA11y(isOpen, dialogRef, onClose);

  useEffect(() => {
    if (isOpen || !focusDesktopAfterClose.current) return;
    focusDesktopAfterClose.current = false;
    const destination = document.querySelector<HTMLElement>('#sidebar nav a[aria-current="page"]')
      ?? document.querySelector<HTMLElement>('#sidebar nav a');
    destination?.focus();
  }, [isOpen]);

  // The desktop rail takes over at this breakpoint; never leave a hidden
  // mobile dialog holding keyboard focus after a resize.
  useEffect(() => {
    if (!isOpen) return;
    const desktop = window.matchMedia(DESKTOP_LAYOUT_QUERY);
    const closeOnDesktop = () => {
      if (!desktop.matches) return;
      focusDesktopAfterClose.current = true;
      onClose();
    };
    closeOnDesktop();
    desktop.addEventListener('change', closeOnDesktop);
    return () => desktop.removeEventListener('change', closeOnDesktop);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return (
    <ModalPortal>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
        <div
          ref={dialogRef}
          id="mobile-navigation"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-navigation-title"
          className="bg-white w-full max-w-xs h-full overflow-y-auto p-4 shadow-xl"
        >
          <div className="flex items-center justify-between mb-4">
            <ModalTitle icon={Menu01Icon} id="mobile-navigation-title" className="oq-dialog__title">Navigation</ModalTitle>
            <button type="button" className="btn-ghost" aria-label="Close navigation" onClick={onClose}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <nav aria-label="Main navigation"><NavigationItems onNavigate={onClose} /></nav>
          {showInstallLink && <div className="mt-4 border-t border-gray-200 pt-4"><InstallExtensionLink onNavigate={onClose} /></div>}
        </div>
      </div>
    </ModalPortal>
  );
}

function NavItem({
  to,
  icon,
  children,
  collapsed,
  alsoMatchPaths,
  onNavigate,
  comingSoon = false,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  collapsed?: boolean;
  alsoMatchPaths?: string[];
  onNavigate?: () => void;
  comingSoon?: boolean;
}) {
  const { pathname } = useLocation();
  const extraMatch = alsoMatchPaths?.some((p) => pathname.startsWith(p)) ?? false;

  const link = (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }: { isActive: boolean }) =>
        clsx(
          'flex items-center rounded-lg text-sm font-medium transition-colors [&>svg]:shrink-0',
          // px-3 (not px-2.5) so the icon column is nav px-4 + item px-3 = 28px
          // from the rail edge, which puts the icon centre at 36px, exactly where
          // the collapsed rail centres a 36px item. Measured 2026-08-05: icons
          // used to slide 3.5px sideways every time the sidebar toggled.
          collapsed ? 'justify-center w-9 h-9 mx-auto' : 'gap-3 px-3 py-2',
          isActive || extraMatch
            // primary-100 + primary-700, not primary-50 + primary-500.
            // Measured 2026-08-05 against the current ground (#f5f7fb):
            // primary-50 separates by 1.028, i.e. no visible pill at all, and
            // primary-500 on primary-50 is 4.25, which fails AA for the label.
            // primary-100 is 1.167 and primary-700 on it is 6.90.
            ? 'bg-primary-100 text-primary-700'
            : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
        )
      }
      aria-label={comingSoon ? `${String(children)}: Coming soon` : collapsed ? String(children) : undefined}
    >
      {icon}
      {!collapsed && children}
      {comingSoon && !collapsed && (
        <HugeiconsIcon icon={Clock01Icon} size={14} strokeWidth={2} color="currentColor" className="ml-auto" aria-hidden="true" />
      )}
    </NavLink>
  );

  return (
    <li>
      {collapsed || comingSoon ? (
        <Tooltip label={comingSoon ? (collapsed ? `${String(children)}: Coming soon` : 'Coming soon') : String(children)} placement={collapsed ? 'right' : 'top'}>
          {link}
        </Tooltip>
      ) : (
        link
      )}
    </li>
  );
}

function ProjectsIcon() {
  return (
    <HugeiconsIcon icon={Folder01Icon} size={20} strokeWidth={2} color="currentColor" aria-hidden="true" />
  );
}

function SettingsIcon() {
  return (
    <HugeiconsIcon icon={Settings02Icon} size={20} strokeWidth={2} color="currentColor" aria-hidden="true" />
  );
}

function IntegrationsIcon() {
  return (
    <HugeiconsIcon icon={PuzzleIcon} size={20} strokeWidth={2} color="currentColor" aria-hidden="true" />
  );
}

function ScheduleIcon() {
  return (
    <HugeiconsIcon icon={Calendar03Icon} size={20} strokeWidth={2} color="currentColor" aria-hidden="true" />
  );
}

function ReportsIcon() {
  return (
    <HugeiconsIcon icon={ChartColumnIcon} size={20} strokeWidth={2} color="currentColor" aria-hidden="true" />
  );
}

function DashboardIcon() {
  return (
    <HugeiconsIcon icon={Home01Icon} size={20} strokeWidth={2} color="currentColor" aria-hidden="true" />
  );
}

function BillingIcon() {
  return (
    <HugeiconsIcon icon={CreditCardIcon} size={20} strokeWidth={2} color="currentColor" aria-hidden="true" />
  );
}

function PageTitle() {
  const { pathname } = useLocation();
  let title: string | null = null;
  if (pathname === '/dashboard') title = 'Dashboard';
  else if (pathname === '/projects') title = 'Projects';
  else if (pathname === '/schedule') title = 'Schedule';
  else if (pathname === '/reports') title = 'Reports';
  else if (pathname === '/integrations') title = 'Integrations';
  else if (pathname === '/settings') title = 'Account';
  else if (pathname === '/billing') title = 'Billing';
  if (!title) return null;
  return <h1 className="page-title truncate">{title}</h1>;
}
